import { IconButton, Menu } from '@efast_public/fjd-component-library';
import { useFlyoutContext } from '../FlyoutContext';
import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useRef,
  useState
} from 'react';
import React from 'react';
import { Variant } from '@efast_public/fjd-component-library/build/src/common/types/variants';
import { MenuChildrenType } from '@efast_public/fjd-component-library/build/src/components/Menu/Menu/Menu.types';

interface TableMenuButtonProps {
  children: MenuChildrenType;
  variant?: Variant;
  autoWidth?: boolean;
}

export function TableMenuButton({
  children,
  variant = 'primary-text',
  autoWidth
}: TableMenuButtonProps) {
  const innerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const id = useId();
  const { registerFlyout, unregisterFlyout, closeOtherFlyouts } =
    useFlyoutContext();

  useEffect(() => {
    registerFlyout(id, () => setIsOpen(false));
    return () => unregisterFlyout(id);
  }, [id, registerFlyout, unregisterFlyout]);

  const handleMenuItemClick = () => {
    setIsOpen(false);
  };

  const wrappedChildren = Children.map(children, (child) => {
    if (isValidElement(child)) {
      const originalOnClick = child.props.onClick;
      return cloneElement(child, {
        ...child.props,
        onClick: (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
          if (originalOnClick) {
            originalOnClick(e);
          }
          handleMenuItemClick();
        }
      });
    }
    return child;
  });

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.stopPropagation();
        }
      }}
      role="presentation"
      tabIndex={-1}
    >
      <IconButton
        label="Open Menu"
        ref={innerRef}
        onClick={() => {
          if (!isOpen) closeOtherFlyouts(id);
          setIsOpen(!isOpen);
        }}
        icon="overflow-menu-vertical"
        variant={variant}
      />
      <Menu
        open={isOpen}
        onClose={() => setIsOpen(false)}
        anchorElement={innerRef?.current}
        autoWidth={autoWidth}
      >
        {wrappedChildren}
      </Menu>
    </div>
  );
}
